'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import Link from 'next/link'
import React from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import PasswordRequirements from '@/components/Auth/PasswordRequirements'
import { REGISTER_PERSON_FIELD_MAP } from '@/constants/auth'
import { CAREER_OPTIONS } from '@/constants/customer'
import { CELL_PHONE_MASK, CPF_MASK } from '@/constants/masks'
import { useRegister } from '@/hooks/business/auth/useRegister'
import { useFormAction } from '@/hooks/shared/useFormAction'
import { type IRegisterPersonForm, RegisterPersonFormSchema } from '@/schemas/auth/authFormSchemas'
import Button from '~/components/Form/Button'
import Checkbox from '~/components/Form/Checkbox'
import Input from '~/components/Form/Input'
import MaskedInput from '~/components/Form/MaskedInput'
import Select from '~/components/Form/Select'

type IFormInput = z.input<typeof RegisterPersonFormSchema>
/** Cadastro de pessoa física (`/cadastro`): abre a sessão e vai para a home; erro de e-mail ou CPF aparece no campo. */
const PersonSignUpForm: React.FC = () => {
    const {
        control,
        register,
        handleSubmit,
        setError,
        getValues,
        formState: { errors },
    } = useForm<IFormInput, unknown, IRegisterPersonForm>({
        resolver: zodResolver(RegisterPersonFormSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            cpf: '',
            email: '',
            phone: '',
            password: '',
            confirmPassword: '',
        },
    })

    const { registerPerson } = useRegister()
    // O handleSubmit entrega o valor já validado (a saída do schema); o tipo do formulário é o da entrada.
    const { handler } = useFormAction((form: IFormInput) => registerPerson(form as IRegisterPersonForm), {
        setError,
        getValues,
        fieldMap: REGISTER_PERSON_FIELD_MAP,
    })

    return (
        <div className="container">
            <form onSubmit={handleSubmit(handler)}>
                <div className="flex gap-2 max-lg:flex-col">
                    <div className="flex flex-col gap-2 border border-gray-cart rounded-2xl p-4 lg:w-2/3">
                        <div className="flex max-md:flex-col gap-2 w-full">
                            <Input
                                labelText="Primeiro nome*"
                                register={register('firstName')}
                                error={errors.firstName?.message}
                                className="w-full"
                            />

                            <Input
                                labelText="Sobrenome*"
                                register={register('lastName')}
                                error={errors.lastName?.message}
                                className="w-full"
                            />
                        </div>

                        <div className="flex max-md:flex-col gap-2 w-full">
                            <MaskedInput
                                control={control}
                                name="cpf"
                                labelText="CPF*"
                                mask={CPF_MASK}
                                className="w-full"
                            />
                            <Input
                                register={register('email')}
                                error={errors.email?.message}
                                labelText="E-mail*"
                                placeholder="Ex.: nome@email.com"
                                className="w-full"
                            />
                        </div>

                        <div className="flex max-md:flex-col gap-2 w-full">
                            <Select
                                labelText="Profissão*"
                                options={CAREER_OPTIONS}
                                register={register('occupation')}
                                error={errors.occupation?.message}
                                className="w-full"
                            />

                            <MaskedInput
                                control={control}
                                name="phone"
                                labelText="Celular*"
                                mask={CELL_PHONE_MASK}
                                placeholder="DDD + número"
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div className="p-4 border border-gray-cart rounded-2xl">
                        <span className="col-span-2 text-lg text-primary font-sora font-bold">CRIAÇÃO DE SENHA</span>

                        <div className="flex max-lg:flex-col-reverse items-center gap-4">
                            <div className="flex flex-col gap-2 w-full">
                                <Input
                                    register={register('password')}
                                    error={errors.password?.message}
                                    labelText="Senha*"
                                    type="password"
                                    className="w-full"
                                />

                                <Input
                                    register={register('confirmPassword')}
                                    error={errors.confirmPassword?.message}
                                    type="password"
                                    labelText="Confirmar senha*"
                                    className="w-full"
                                />
                            </div>

                            <PasswordRequirements />
                        </div>
                    </div>
                </div>

                <div className="pb-10 lg:mr-auto lg:pl-28 mt-6">
                    <Checkbox
                        name="terms"
                        register={register('terms')}
                        error={errors.terms?.message}
                        className="flex-col"
                        labelText={
                            <span className="text-primary font-sora text-sm ml-2">
                                Li e aceito os{' '}
                                <Link href="/termos-e-condicoes" className="underline">
                                    Termos e condições
                                </Link>{' '}
                                e autorizo o uso de meus dados de acordo com a{' '}
                                <Link href="/politica-de-privacidade" className="underline">
                                    Declaração de privacidade
                                </Link>
                                .
                            </span>
                        }
                    />
                    <div className="pt-2" />
                    <Button size="lg" color="tertiary" type="submit">
                        CADASTRAR
                    </Button>
                </div>
            </form>
        </div>
    )
}

export default PersonSignUpForm
